import React from 'react';
import PropTypes from 'prop-types';

import Rating from '../../../cards/PlayerCard/SharedComponents/Rating';
import { utrTypes, sportTypeIds } from '../../../../utils/eventConstants';
import { MatchFormatTypes } from '../../../../utils/postScoreConstants';
import { RatingDisplayStates } from '../../../../utils/constants';
import { getRatingDetails, setRatingChoice } from '../../../../utils';
import styles from './playersList.scss';

const DELTA_ARROW_UP = '/img/v3/icons/icon-delta-up-green.svg';
const DELTA_ARROW_DOWN = '/img/v3/icons/icon-delta-down-red.svg';

const DeltaIndicator = ({ delta, 'data-testid': dataTestId }) => {
    const value = Number(delta);
    if (delta === null || delta === undefined || !Number.isFinite(value) || value === 0) {
        return null;
    }
    const isUp = value > 0;
    return (
        <span
            className={`${styles.delta} ${isUp ? styles.deltaUp : styles.deltaDown}`}
            data-testid={dataTestId || undefined}
        >
            <img
                className={`${styles.deltaArrow} ${isUp ? styles.deltaArrowUp : styles.deltaArrowDown}`}
                src={isUp ? DELTA_ARROW_UP : DELTA_ARROW_DOWN}
                alt=""
                data-testid={dataTestId && `${dataTestId}.arrow`}
            />
            <span className={styles.deltaValue} data-testid={dataTestId && `${dataTestId}.value`}>{Math.abs(value).toFixed(2)}</span>
        </span>
    );
};

DeltaIndicator.propTypes = {
    delta: PropTypes.number,
    'data-testid': PropTypes.string,
};

DeltaIndicator.defaultProps = {
    delta: null,
    'data-testid': null,
};

// Passes the exact prop set ImportedEventPlayerRow gives the shared Rating (Utr + UtrLabel), so
// saved-list ratings read identically to the 3rd-Party/CPP rows — verified icon, status/reliability
// label, hover tooltip chip. The weekly delta rides as a sibling rather than a Rating prop so the
// shared component is never modified, and it is gated by the SAME fully-reliable state getRatingDetails
// resolves for the label, so a delta appears only when the label reads fully reliable.
const RatingCell = ({ player, ratingType, 'data-testid': dataTestId }) => {
    const isSingles = ratingType === MatchFormatTypes.SINGLES;
    const { displayState } = getRatingDetails(setRatingChoice({ player, ratingType, utrType: utrTypes.SANCTIONED }));
    const delta = isSingles ? player.singlesDelta : player.doublesDelta;
    const showDelta = displayState === RatingDisplayStates.FULLY_RELIABLE;

    return (
        <div className={styles.ratingCell} data-testid={dataTestId || undefined}>
            <Rating
                player={player}
                ratingType={ratingType}
                utrType={utrTypes.SANCTIONED}
                ratingStatus={isSingles ? player.ratingStatusSingles : player.ratingStatusDoubles}
                sportTypeId={sportTypeIds.TENNIS}
                showTypeDots={false}
                showReliability
                hideHistoricMarker
                historicVerifiedIcon
                verifiedTooltip
                verifiedIconVariant="black"
                statusTooltip
                emphasizeValue
                data-testid={dataTestId}
            />
            {showDelta && <DeltaIndicator delta={delta} data-testid={dataTestId && `${dataTestId}.delta`} />}
        </div>
    );
};

RatingCell.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    ratingType: PropTypes.oneOf([MatchFormatTypes.SINGLES, MatchFormatTypes.DOUBLES]).isRequired,
    'data-testid': PropTypes.string,
};

RatingCell.defaultProps = {
    'data-testid': null,
};

export default RatingCell;
